import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import KanbanCard from './KanbanCard';
import { Typography, IconButton, Tooltip } from '@material-ui/core';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';

const useStyles = makeStyles(theme => ({
  column: {
    backgroundColor: theme.palette.aeros.sidebar,
    border: `1px solid ${theme.palette.aeros.border}`,
    borderRadius: 14,
    // Mockup: lanes dividem a largura igualmente (flex:1); com muitas
    // colunas o board rola horizontalmente e cada lane segura 280px.
    flex: '1 0 280px',
    minWidth: 280,
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
  },
  laneDot: props => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: props.color || theme.palette.aeros.textTertiary,
    flexShrink: 0,
  }),
  columnTitle: {
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  cardList: {
    flexGrow: 1,
    overflowY: 'auto',
    ...theme.scrollbarStyles,
    maxHeight: 'calc(var(--vh-full) - 240px)',
    // Coluna vazia precisa de área de drop: sem min-height o Droppable fica
    // com 0px de altura e o react-beautiful-dnd nunca registra o destino
    // (não dava para mover um ticket para uma coluna recém-criada).
    minHeight: 120,
  },
  totalValue: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.aeros.textTertiary,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  laneCount: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.aeros.textTertiary,
    flexShrink: 0,
  },
  columnHeader: {
    marginBottom: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  headerRight: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
  },
  dragHandle: {
    cursor: 'grab',
    color: theme.palette.aeros.textTertiary,
    padding: theme.spacing(0.25),
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      color: theme.palette.text.secondary,
    },
    '& .MuiSvgIcon-root': { fontSize: 16 },
  },
  columnContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
}));

const KanbanColumn = ({ id, title, tickets, color, index, updateTicket, isAdmin, onDeleteLane }) => {
  const classes = useStyles({ color });

  const totalValue = tickets.reduce((acc, ticket) => {
    const customFields = ticket.contact.extraInfo || [];
    const valueField = customFields.find(field => field.name === 'valor');
    const opportunityValue = valueField ? parseFloat(valueField.value) : 0;
    return acc + opportunityValue;
  }, 0);

  return (
    <Draggable draggableId={id} index={index}>
      {(provided, snapshot) => (
        <div
          className={classes.column}
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          <div className={classes.columnHeader}>
            <span className={classes.laneDot} />
            <Typography className={classes.columnTitle}>{title}</Typography>
            <Typography className={classes.laneCount}>{tickets.length}</Typography>
            <div className={classes.headerRight}>
              <Typography className={classes.totalValue}>
                R$ {totalValue.toFixed(2)}
              </Typography>
              {/* A coluna padrao (lane0) nao e uma tag, entao nao tem o que
                  excluir; nas demais, excluir a coluna exclui a tag Kanban. */}
              {isAdmin && onDeleteLane && id !== 'lane0' && (
                <Tooltip title="Excluir coluna">
                  <IconButton
                    size="small"
                    className={classes.dragHandle}
                    aria-label="Excluir coluna"
                    onClick={() => onDeleteLane({ id, title })}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {isAdmin && (
                <div
                  {...provided.dragHandleProps}
                  className={classes.dragHandle}
                >
                  <DragIndicatorIcon fontSize="small" />
                </div>
              )}
            </div>
          </div>
          <Droppable droppableId={id} type="CARD">
            {(provided, snapshot) => (
              <div
                className={classes.cardList}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {tickets.map((ticket, index) => (
                  <KanbanCard
                    key={ticket.id}
                    ticket={ticket}
                    index={index}
                    updateTicket={updateTicket}
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      )}
    </Draggable>
  );
};

export default KanbanColumn;
